import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import ReceiptEditor from "@/pages/ReceiptEditor";
import ReceiptDetail from "@/pages/ReceiptDetail";
import ExportCenter from "@/pages/ExportCenter";
import ManageLists from "@/pages/ManageLists";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import RegisterSteuerberater from "@/pages/RegisterSteuerberater";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import EmailVerify from "@/pages/EmailVerify";
import UserProfile from "@/pages/UserProfile";
import AdminDashboard from "@/pages/AdminDashboard";
import Billing from "@/pages/Billing";
import Organization from "@/pages/Organization";
import AcceptInvite from "@/pages/AcceptInvite";
import ConsentLink from "@/pages/ConsentLink";
import Activity from "@/pages/Activity";
import Handbuch from "@/pages/Handbuch";
import EmailTemplates from "@/pages/EmailTemplates";
import Referrals from "@/pages/Referrals";
import MeinWalter from "@/pages/MeinWalter";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import UserbackWidget from "@/components/UserbackWidget";
import WalterWidget from "@/components/WalterWidget";
import IdleTimeout from "@/components/IdleTimeout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function ProtectedRoute({ children, allowWhenGated = false }) {
  const { user, loading, impersonating } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  // Subscription paywall: trial elapsed / no active subscription (non-exempt).
  // Admins viewing a user (impersonation) are never gated.
  if (user.requires_subscription && !allowWhenGated && !impersonating) {
    return <Navigate to="/abo" replace />;
  }
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to={user.role === "admin" ? "/administration" : "/"} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/registrieren" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/registrieren/stb" element={<PublicRoute><RegisterSteuerberater /></PublicRoute>} />
      <Route path="/passwort-vergessen" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/passwort-zuruecksetzen" element={<ResetPassword />} />
      <Route path="/email-bestaetigen" element={<EmailVerify />} />
      <Route path="/einladung" element={<AcceptInvite />} />
      <Route path="/verknuepfung" element={<ConsentLink />} />

      {/* Protected app routes */}
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/neu" element={<ProtectedRoute><ReceiptEditor /></ProtectedRoute>} />
      <Route path="/beleg/:id" element={<ProtectedRoute><ReceiptDetail /></ProtectedRoute>} />
      <Route path="/beleg/:id/bearbeiten" element={<ProtectedRoute><ReceiptEditor /></ProtectedRoute>} />
      <Route path="/export" element={<ProtectedRoute><ExportCenter /></ProtectedRoute>} />
      <Route path="/stammdaten" element={<ProtectedRoute><ManageLists /></ProtectedRoute>} />
      <Route path="/abo" element={<ProtectedRoute allowWhenGated><Billing /></ProtectedRoute>} />
      <Route path="/benutzer" element={<ProtectedRoute allowWhenGated><UserProfile /></ProtectedRoute>} />
      <Route path="/organisation" element={<ProtectedRoute allowWhenGated><Organization /></ProtectedRoute>} />
      <Route path="/aktivitaet" element={<ProtectedRoute allowWhenGated><Activity /></ProtectedRoute>} />
      <Route path="/handbuch" element={<ProtectedRoute allowWhenGated><Handbuch /></ProtectedRoute>} />
      <Route path="/empfehlungen" element={<ProtectedRoute allowWhenGated><Referrals /></ProtectedRoute>} />
      <Route path="/administration" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/mein-walter" element={<AdminRoute><MeinWalter /></AdminRoute>} />
      <Route path="/mails" element={<AdminRoute><EmailTemplates /></AdminRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <AppRoutes />
          <UserbackWidget />
          <WalterWidget />
          <IdleTimeout />
        </AuthProvider>
        <Toaster position="top-right" richColors closeButton />
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
